import React, { useState, useEffect } from "react";
import axios from "axios";
import Navbar from "../components/layout/Navbar";
import Footer from "../components/layout/Footer";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  MapPin,
  Layers,
  Download,
  ExternalLink,
  Map as MapIcon,
  Building,
  FileText,
  Users,
  BarChart3,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MapsPage = () => {
  const [selectedZone, setSelectedZone] = useState("all");
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    axios
      .get(`${API}/content/map-zones`)
      .then((res) => {
        if (cancelled) return;
        // Only show active zones on the public page
        const active = (res.data || []).filter((z) => z.is_active !== false);
        setZones(active);
      })
      .catch(() => {
        if (!cancelled) setZones([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const displayZones =
    selectedZone === "all"
      ? zones
      : zones.filter((z) => (z.code || z.id) === selectedZone);

  // Aggregate stats
  const totalArea = zones.reduce((s, z) => s + (Number(z.area_hectare) || 0), 0);
  const totalVillages = zones.reduce(
    (s, z) => s + ((z.villages && z.villages.length) || 0),
    0
  );
  const avgPooled =
    zones.length > 0
      ? Math.round(
          zones.reduce((s, z) => s + (Number(z.pooled_percentage) || 0), 0) /
            zones.length
        )
      : 0;

  return (
    <div className="min-h-screen bg-slate-50" data-testid="maps-page">
      <Navbar />

      {/* Hero */}
      <section className="bg-gradient-to-r from-[#4E80FF] to-[#2952CC] py-12">
        <div className="container-main">
          <div className="flex items-center gap-2 text-white/80 mb-4">
            <MapIcon className="w-5 h-5" />
            <span className="text-sm font-medium">Official DDA Zone Maps</span>
          </div>
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-white mb-4">
            Land Pooling Zone Maps
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mb-6">
            Access official DDA maps for all land pooling zones including zonal
            plans, revenue maps, and sector delineation plans.
          </p>
          <div className="flex flex-wrap gap-4">
            <a
              href="https://online.dda.org.in/landpooling/AppForm/LandPoolMap.aspx"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-white text-[#4E80FF] px-6 py-3 rounded-lg font-medium hover:bg-slate-100 transition-colors"
              data-testid="visit-dda-portal-hero"
            >
              <ExternalLink className="w-4 h-4" />
              Visit DDA Maps Portal
            </a>
          </div>
        </div>
      </section>

      {/* Quick Stats */}
      <section className="py-8 bg-white border-b">
        <div className="container-main">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <p className="text-3xl font-bold text-[#4E80FF]">{zones.length}</p>
              <p className="text-sm text-slate-600">Planning Zones</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-[#4E80FF]">{totalVillages}</p>
              <p className="text-sm text-slate-600">Villages Listed</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-[#4E80FF]">
                {totalArea > 0 ? `${(totalArea / 1000).toFixed(1)}K` : "—"}
              </p>
              <p className="text-sm text-slate-600">Hectares</p>
            </div>
            <div className="text-center">
              <p className="text-3xl font-bold text-[#4E80FF]">
                {avgPooled > 0 ? `~${avgPooled}%` : "—"}
              </p>
              <p className="text-sm text-slate-600">Avg. Pooled</p>
            </div>
          </div>
        </div>
      </section>

      {/* Zone Filter */}
      <section className="py-6 bg-slate-50">
        <div className="container-main">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Layers className="w-5 h-5 text-slate-500" />
              <span className="text-sm font-medium text-slate-700">
                Filter by Zone:
              </span>
            </div>
            <Select value={selectedZone} onValueChange={setSelectedZone}>
              <SelectTrigger className="w-[260px]" data-testid="zone-selector">
                <SelectValue placeholder="All Zones" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Zones</SelectItem>
                {zones.map((z) => (
                  <SelectItem key={z.id} value={z.code || z.id}>
                    {z.zone_name}
                    {z.description ? ` (${z.description.split(" — ")[1] || ""})` : ""}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </section>

      {/* Zone Cards */}
      <section className="py-8">
        <div className="container-main">
          {loading ? (
            <div className="flex items-center justify-center h-48">
              <div className="animate-spin rounded-full h-10 w-10 border-4 border-primary-700 border-t-transparent" />
            </div>
          ) : displayZones.length === 0 ? (
            <div className="text-center text-slate-500 py-16">
              No zones available yet.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {displayZones.map((zone) => (
                <Card
                  key={zone.id}
                  className="card-hover overflow-hidden"
                  data-testid={`zone-card-${zone.id}`}
                >
                  <div
                    className="h-2"
                    style={{ backgroundColor: zone.color || "#4E80FF" }}
                  />
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="font-heading text-xl font-bold text-slate-900">
                          {zone.zone_name}
                        </h3>
                        <p className="text-sm text-slate-600 mt-1">
                          {zone.description || zone.region}
                        </p>
                      </div>
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center"
                        style={{
                          backgroundColor: `${zone.color || "#4E80FF"}20`,
                        }}
                      >
                        <MapPin
                          className="w-5 h-5"
                          style={{ color: zone.color || "#4E80FF" }}
                        />
                      </div>
                    </div>

                    {/* Stats */}
                    <div className="grid grid-cols-3 gap-3 mb-4">
                      <div className="bg-slate-50 rounded-lg p-3 text-center">
                        <p className="text-lg font-bold text-slate-900">
                          {zone.area_display || `${zone.area_hectare || 0} ha`}
                        </p>
                        <p className="text-xs text-slate-500">Area</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3 text-center">
                        <p className="text-lg font-bold text-slate-900">
                          {zone.pooled_percentage != null
                            ? `${zone.pooled_percentage}%`
                            : "—"}
                        </p>
                        <p className="text-xs text-slate-500">Pooled</p>
                      </div>
                      <div className="bg-slate-50 rounded-lg p-3 text-center">
                        <p className="text-lg font-bold text-slate-900">
                          {zone.sectors ?? "—"}
                        </p>
                        <p className="text-xs text-slate-500">Sectors</p>
                      </div>
                    </div>

                    {/* Villages */}
                    {Array.isArray(zone.villages) && zone.villages.length > 0 && (
                      <div className="mb-4">
                        <p className="text-xs text-slate-500 mb-2">Villages:</p>
                        <div className="flex flex-wrap gap-1">
                          {zone.villages.slice(0, 3).map((village, idx) => (
                            <Badge
                              key={idx}
                              variant="secondary"
                              className="text-xs"
                            >
                              {village}
                            </Badge>
                          ))}
                          {zone.villages.length > 3 && (
                            <Badge variant="outline" className="text-xs">
                              +{zone.villages.length - 3} more
                            </Badge>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Resource Links */}
                    {Array.isArray(zone.resources) && zone.resources.length > 0 && (
                      <div className="space-y-2 pt-4 border-t">
                        <p className="text-xs font-medium text-slate-700 mb-2">
                          Official Maps:
                        </p>
                        {zone.resources.map((r, idx) => (
                          <a
                            key={idx}
                            href={r.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center justify-between p-2 bg-slate-50 rounded hover:bg-slate-100 transition-colors"
                            data-testid={`zone-${zone.id}-resource-${idx}`}
                          >
                            <span className="text-sm text-slate-700">
                              {r.name || r.label || "Resource"}
                            </span>
                            {r.url && r.url.endsWith(".pdf") ? (
                              <Download className="w-4 h-4 text-[#4E80FF]" />
                            ) : (
                              <ExternalLink className="w-4 h-4 text-[#4E80FF]" />
                            )}
                          </a>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Master Plan & General Resources (static) */}
      <section className="py-12 bg-white">
        <div className="container-main">
          <h2 className="font-heading text-2xl font-bold text-slate-900 mb-6">
            Master Plan & General Resources
          </h2>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <a
              href="https://online.dda.org.in/landpooling/AppForm/Maps/Master%20Plan.jpg"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-5 border rounded-xl hover:border-[#4E80FF] hover:bg-blue-50 transition-colors"
            >
              <div className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <MapIcon className="w-6 h-6 text-[#4E80FF]" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-slate-900">Master Plan of Delhi</p>
                <p className="text-sm text-slate-500">Land Use Plan 2021</p>
              </div>
              <ExternalLink className="w-5 h-5 text-slate-400" />
            </a>

            <a
              href="https://online.dda.org.in/landpooling/AppForm/Maps/Villages_under_Land_Pooling__2022.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-5 border rounded-xl hover:border-[#4E80FF] hover:bg-blue-50 transition-colors"
            >
              <div className="w-12 h-12 rounded-lg bg-green-100 flex items-center justify-center">
                <Users className="w-6 h-6 text-green-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-slate-900">
                  Villages Under Land Pooling
                </p>
                <p className="text-sm text-slate-500">Complete village map 2022</p>
              </div>
              <Download className="w-5 h-5 text-slate-400" />
            </a>

            <a
              href="https://online.dda.org.in/landpooling/AppForm/Maps/Planning%20Zone%20Boundaries.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-5 border rounded-xl hover:border-[#4E80FF] hover:bg-blue-50 transition-colors"
            >
              <div className="w-12 h-12 rounded-lg bg-orange-100 flex items-center justify-center">
                <Layers className="w-6 h-6 text-orange-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-slate-900">
                  Planning Zone Boundaries
                </p>
                <p className="text-sm text-slate-500">Zone demarcation map</p>
              </div>
              <Download className="w-5 h-5 text-slate-400" />
            </a>

            <a
              href="https://online.dda.org.in/landpooling/AppForm/docs/Updated_Status_of_Sector-_wise_participation_in_LP_upto_31_07_2025.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-5 border rounded-xl hover:border-[#4E80FF] hover:bg-blue-50 transition-colors"
            >
              <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                <BarChart3 className="w-6 h-6 text-purple-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-slate-900">
                  Sector-wise Participation Status
                </p>
                <p className="text-sm text-slate-500">Updated till July 2025</p>
              </div>
              <Download className="w-5 h-5 text-slate-400" />
            </a>

            <a
              href="https://online.dda.org.in/landpooling/AppForm/docs/SECTOR_LAYOUT_PPT_English.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-5 border rounded-xl hover:border-[#4E80FF] hover:bg-blue-50 transition-colors"
            >
              <div className="w-12 h-12 rounded-lg bg-cyan-100 flex items-center justify-center">
                <FileText className="w-6 h-6 text-cyan-600" />
              </div>
              <div className="flex-1">
                <p className="font-semibold text-slate-900">
                  Sample Sector Layout Plan
                </p>
                <p className="text-sm text-slate-500">Reference layout example</p>
              </div>
              <Download className="w-5 h-5 text-slate-400" />
            </a>

            <a
              href="https://online.dda.org.in/landpooling/AppForm/default.aspx"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-5 bg-[#4E80FF] text-white rounded-xl hover:bg-[#2952CC] transition-colors"
            >
              <div className="w-12 h-12 rounded-lg bg-white/20 flex items-center justify-center">
                <Building className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <p className="font-semibold">DDA Land Pooling Portal</p>
                <p className="text-sm text-white/80">Official application portal</p>
              </div>
              <ExternalLink className="w-5 h-5 text-white/80" />
            </a>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 bg-slate-900">
        <div className="container-main text-center">
          <h2 className="font-heading text-3xl font-bold text-white mb-4">
            Check Your Land Eligibility
          </h2>
          <p className="text-slate-300 mb-8 max-w-xl mx-auto">
            Visit the official DDA portal to verify if your land falls within a
            land pooling zone and submit your application.
          </p>
          <a
            href="https://online.dda.org.in/landpooling/AppForm/default.aspx"
            target="_blank"
            rel="noopener noreferrer"
          >
            <Button
              size="lg"
              className="bg-[#4E80FF] hover:bg-[#2952CC]"
              data-testid="visit-dda-portal-cta"
            >
              <MapPin className="w-5 h-5 mr-2" />
              Visit DDA Portal
            </Button>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default MapsPage;
